import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Star,
  MapPin,
  Phone,
  Globe,
  Clock,
  Mail,
  AlertCircle,
  ThumbsUp,
  CheckCircle,
  Loader2,
  ArrowLeft,
  ExternalLink,
  MessageSquare,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [service, setService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: '',
    comment: '',
    would_recommend: true,
    pros: [],
    cons: []
  });
  const [prosInput, setProsInput] = useState('');
  const [consInput, setConsInput] = useState('');

  useEffect(() => {
    fetchService();
    fetchReviews();
  }, [serviceId]);

  const fetchService = async () => {
    try {
      const response = await axios.get(`${API}/services/${serviceId}`);
      setService(response.data);
    } catch (error) {
      toast.error('Service not found');
      navigate('/services');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/services/${serviceId}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) {
      toast.error('Please write a review');
      return;
    }
    
    setSubmitting(true);
    try {
      const reviewData = {
        ...newReview,
        pros: prosInput.split(',').map(s => s.trim()).filter(Boolean),
        cons: consInput.split(',').map(s => s.trim()).filter(Boolean)
      };
      
      await axios.post(`${API}/services/${serviceId}/reviews`, reviewData);
      toast.success('Review submitted!');
      setReviewDialogOpen(false);
      setNewReview({
        rating: 5,
        title: '',
        comment: '',
        would_recommend: true,
        pros: [],
        cons: []
      });
      setProsInput('');
      setConsInput('');
      fetchService();
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API}/services/${serviceId}/reviews/${reviewId}/helpful`);
      fetchReviews();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('You already marked this as helpful');
      }
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onSelect && onSelect(star)}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
            disabled={!interactive}
          >
            <Star
              className={`w-5 h-5 ${
                star <= rating
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-muted-foreground/30'
              } ${interactive ? 'hover:text-amber-400' : ''}`}
            />
          </button>
        ))}
      </div>
    );
  };

  const getRatingBreakdown = () => {
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => {
      breakdown[r.rating] = (breakdown[r.rating] || 0) + 1;
    });
    return breakdown;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!service) return null;

  const ratingBreakdown = getRatingBreakdown();
  const recommendPercent = reviews.length > 0 
    ? Math.round((reviews.filter(r => r.would_recommend).length / reviews.length) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/services')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Services
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Image */}
                  <div className="w-full md:w-48 h-48 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                    {service.image_url ? (
                      <img 
                        src={service.image_url} 
                        alt={service.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Award className="w-16 h-16 text-muted-foreground/30" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h1 className="text-2xl font-heading font-medium">{service.name}</h1>
                          {service.license_verified && (
                            <CheckCircle className="w-5 h-5 text-primary" title="Verified" />
                          )}
                        </div>
                        <Badge variant="secondary" className="mt-1 capitalize">
                          {service.category.replace('_', ' ')}
                        </Badge>
                      </div>
                      {service.price_range && (
                        <span className="text-2xl font-medium text-primary">{service.price_range}</span>
                      )}
                    </div>

                    {/* Rating Summary */}
                    <div className="flex items-center gap-4 my-4">
                      {renderStars(service.average_rating)}
                      <span className="text-xl font-medium">{service.average_rating.toFixed(1)}</span>
                      <span className="text-muted-foreground">
                        ({service.review_count} reviews)
                      </span>
                    </div>

                    {recommendPercent > 0 && (
                      <div className="flex items-center gap-2 text-primary mb-4">
                        <ThumbsUp className="w-5 h-5" />
                        <span className="font-medium">{recommendPercent}% recommend this service</span>
                      </div>
                    )}

                    <p className="text-muted-foreground">{service.description}</p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {service.accepts_emergency && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          24/7 Emergency
                        </Badge>
                      )}
                      {service.is_mobile && (
                        <Badge variant="secondary">Mobile Service</Badge>
                      )}
                      {service.specialties?.map((spec, idx) => (
                        <Badge key={spec} variant="outline">{spec}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reviews Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Reviews</CardTitle>
                {user && (
                  <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary" data-testid="write-review-btn">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Write a Review
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Write a Review</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Your Rating</label>
                          {renderStars(newReview.rating, true, (rating) => 
                            setNewReview({ ...newReview, rating })
                          )}
                        </div>
                        
                        <Input
                          placeholder="Review Title (optional)"
                          value={newReview.title}
                          onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                        />
                        
                        <Textarea
                          placeholder="Share your experience..."
                          value={newReview.comment}
                          onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                          rows={4}
                        />
                        
                        <Input
                          placeholder="Pros (comma separated)"
                          value={prosInput}
                          onChange={(e) => setProsInput(e.target.value)}
                        />
                        
                        <Input
                          placeholder="Cons (comma separated)"
                          value={consInput}
                          onChange={(e) => setConsInput(e.target.value)}
                        />
                        
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newReview.would_recommend}
                            onChange={(e) => setNewReview({ ...newReview, would_recommend: e.target.checked })}
                            className="rounded"
                          />
                          <span className="text-sm">I would recommend this service</span>
                        </label>
                        
                        <Button 
                          className="w-full btn-primary" 
                          onClick={handleSubmitReview}
                          disabled={submitting}
                        >
                          {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                          Submit Review
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {/* Rating Breakdown */}
                {reviews.length > 0 && (
                  <div className="mb-6 p-4 bg-muted/50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-4xl font-heading font-medium mb-1">
                          {service.average_rating.toFixed(1)}
                        </div>
                        {renderStars(service.average_rating)}
                        <p className="text-sm text-muted-foreground mt-1">
                          Based on {reviews.length} reviews
                        </p>
                      </div>
                      <div className="space-y-1">
                        {[5, 4, 3, 2, 1].map((stars) => (
                          <div key={stars} className="flex items-center gap-2">
                            <span className="text-xs w-3">{stars}</span>
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-amber-400 rounded-full"
                                style={{ 
                                  width: `${reviews.length > 0 ? (ratingBreakdown[stars] / reviews.length) * 100 : 0}%` 
                                }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-6">
                              {ratingBreakdown[stars]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Reviews List */}
                {reviews.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b pb-6 last:border-0" data-testid={`review-${review.id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={review.user_avatar} />
                              <AvatarFallback>{review.username?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{review.username}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {renderStars(review.rating)}
                            {review.would_recommend && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <ThumbsUp className="w-3 h-3" />
                                Recommends
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {review.title && (
                          <h4 className="font-medium mb-2">{review.title}</h4>
                        )}
                        
                        <p className="text-muted-foreground mb-3">{review.comment}</p>
                        
                        {/* Pros & Cons */}
                        {(review.pros?.length > 0 || review.cons?.length > 0) && (
                          <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                            {review.pros?.length > 0 && (
                              <div>
                                <p className="font-medium text-primary mb-1">Pros</p>
                                <ul className="list-disc list-inside text-muted-foreground">
                                  {review.pros.map((pro, idx) => (
                                    <li key={`pro-${idx}-${pro}`}>{pro}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {review.cons?.length > 0 && (
                              <div>
                                <p className="font-medium text-destructive mb-1">Cons</p>
                                <ul className="list-disc list-inside text-muted-foreground">
                                  {review.cons.map((con, idx) => (
                                    <li key={`con-${idx}-${con}`}>{con}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Owner Response */}
                        {review.response_from_owner && (
                          <div className="bg-muted/50 rounded-lg p-3 mt-3">
                            <p className="text-sm font-medium mb-1">Response from owner</p>
                            <p className="text-sm text-muted-foreground">{review.response_from_owner}</p>
                          </div>
                        )}
                        
                        <button
                          onClick={() => handleMarkHelpful(review.id)}
                          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mt-3"
                        >
                          <ThumbsUp className="w-4 h-4" />
                          Helpful ({review.helpful_count})
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Contact Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {service.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p>{service.address}</p>
                      <p>{service.city}{service.state && `, ${service.state}`} {service.zip_code}</p>
                    </div>
                  </div>
                )}
                
                {service.phone && (
                  <a 
                    href={`tel:${service.phone}`}
                    className="flex items-center gap-3 hover:text-primary"
                  >
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    {service.phone}
                  </a>
                )}
                
                {service.email && (
                  <a 
                    href={`mailto:${service.email}`}
                    className="flex items-center gap-3 hover:text-primary"
                  >
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    {service.email}
                  </a>
                )}
                
                {service.website && (
                  <a 
                    href={service.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:text-primary"
                  >
                    <Globe className="w-5 h-5 text-muted-foreground" />
                    Visit Website
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                
                {service.hours_of_operation && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <p>{service.hours_of_operation}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Services Offered */}
            {service.services_offered?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Services Offered</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.services_offered.map((svc) => (
                      <li key={`svc-${svc}`} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        {svc}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {service.certifications?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Certifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {service.certifications.map((cert) => (
                      <Badge key={`cert-${cert}`} variant="outline" className="flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        {cert}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
